import {Action, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {MediaStateModel} from '@store/media/media-state.model';
import {Observable, from, of} from 'rxjs';
import {MediaService} from '@services/media/media.service';
import {concatMap, finalize, map, mergeMap, switchMap, tap, toArray} from 'rxjs/operators';
import {
    ChangeCurrentMediaPage,
    ChangeMediaPerPage, ChangeSearchMediaParam, CleanMedias,
    CleanSearchMediaParam,
    CleanStoredMedia, DeleteFolders,
    DeleteMedia, EditMedia,
    FetchMedia, ResetMediaState,
    SearchMedia, SetMediaDefaultPerPage,
    SetMediaPerPage,
    SetSelectedMediaType, SetTargetFolderPath, SetProofOnly, SortMediasContent,
    ToggleMediaView, UpdateMediaFromApi, UpdateMediaProofFlags
} from '@store/media/media.actions';
import {itemsPerPage} from '@shared/helpers';
import {CookieService} from 'ngx-cookie';
import {ViewType} from '@shared/enums/view-type.enum';
import {ToggleGlobalLoader, ToggleMediaLoader} from '@store/loader';
import {UserSelectors} from '@store/user';

const initialState: MediaStateModel = {
    currentPage: 1,
    mediaPerPage: itemsPerPage[5],
    searchParam: '',
    contentItemAmount: 0,
    selectedMediaType: '',
    folders: [],
    medias: [],
    viewType: ViewType.list,
    order: 'DESC',
    sort: 'created_at',
    loader: false,
    currentDirectory: null,
    onlyMedias: false,
    proofOnly: false
};

@State<MediaStateModel>({
    name: 'mediaState',
    defaults: {...initialState}
})

@Injectable()
export class MediaState {
    private fetchRevision = 0;
    constructor(
        private mediaService: MediaService,
        private cookie: CookieService,
        private store: Store
    ) {
    }

    @Action(FetchMedia, {cancelUncompleted: true})
    fetchMedia(ctx: StateContext<MediaStateModel>): Observable<any> {
        const limit = ctx.getState().mediaPerPage;
        const skip = limit * (ctx.getState().currentPage - 1);
        const search = ctx.getState().searchParam;
        const sort = ctx.getState().sort;
        const order = ctx.getState().order;
        const folderId = ctx.getState().currentDirectory?.id as number;
        const onlyMedias = false;
        const mediaType = ctx.getState().selectedMediaType;
        const mediaTypes = mediaType ? [mediaType] : '';
        const proofOnly = ctx.getState().proofOnly &&
            folderId === this.store.selectSnapshot(UserSelectors.getMediaHomeDirId);
        const revision = ++this.fetchRevision;

        // emit loader
        ctx.patchState({loader: true});
        this.store.dispatch(new ToggleMediaLoader(true));

        const request$ = proofOnly
            ? this.mediaService.getAllMedia({
                limit, skip, order: `${order === 'DESC' ? '-' : ''}${sort}`,
                search, media_type: mediaType, proofEnabled: true
            }).pipe(map(response => ({content: response.rows, folders: [], itemsCount: response.total})))
            : this.mediaService.fetchFolderContent(folderId, {
                limit, skip, sort, order
            }, mediaTypes, search, onlyMedias, false);

        return request$
            .pipe(
                switchMap((content) => {
                    const state = ctx.getState();
                    const lastPage = Math.max(Math.ceil(content.itemsCount / limit), 1);
                    if (state.currentPage > lastPage) {
                        ctx.patchState({currentPage: lastPage});
                        return this.fetchMedia(ctx);
                    }
                    ctx.setState({
                        ...state, medias: content.content, loader: false,
                        folders: content.folders, contentItemAmount: content.itemsCount
                    });
                    return of(undefined);
                }),
                finalize(() => {
                    if (revision === this.fetchRevision) {
                        ctx.patchState({loader: false});
                        this.store.dispatch(new ToggleMediaLoader(false));
                    }
                })
            );
    }

    @Action(UpdateMediaProofFlags)
    updateMediaProofFlags(ctx: StateContext<MediaStateModel>, {ids, proofEnabled}: UpdateMediaProofFlags): void {
        const state = ctx.getState();
        const updated = new Set(ids);
        const medias = state.medias.map(media => updated.has(media.id) ? {...media, proofEnabled} : media);
        const visibleMedias = state.proofOnly ? medias.filter(media => media.proofEnabled) : medias;
        const removedCount = medias.length - visibleMedias.length;
        ctx.patchState({
            medias: visibleMedias,
            contentItemAmount: Math.max(state.contentItemAmount - removedCount, 0),
            currentPage: this.getCurrentPageAfterDelete(state.currentPage, state.mediaPerPage, state.contentItemAmount, removedCount)
        });
    }

    @Action(SetProofOnly)
    setProofOnly(ctx: StateContext<MediaStateModel>, {enabled}: SetProofOnly): Observable<void> | void {
        const state = ctx.getState();
        const homeId = this.store.selectSnapshot(UserSelectors.getMediaHomeDirId);
        const proofOnly = enabled && homeId !== null && state.currentDirectory?.id === homeId;
        if (state.proofOnly === proofOnly) return;
        ctx.patchState({proofOnly, currentPage: 1, medias: [], folders: [], contentItemAmount: 0});
        return ctx.dispatch(new FetchMedia());
    }

    @Action(UpdateMediaFromApi)
    updateMediaFromApi(ctx: StateContext<MediaStateModel>, {media}: UpdateMediaFromApi): Observable<void> | void {
        const state = ctx.getState();
        if (state.proofOnly && !media.proofEnabled && state.medias.some(item => item.id === media.id)) {
            ctx.patchState({
                medias: state.medias.filter(item => item.id !== media.id),
                contentItemAmount: Math.max(state.contentItemAmount - 1, 0),
                currentPage: this.getCurrentPageAfterDelete(state.currentPage, state.mediaPerPage, state.contentItemAmount, 1)
            });
            return ctx.dispatch(new FetchMedia());
        }
        ctx.patchState({
            medias: ctx.getState().medias.map(item => item.id === media.id ? {...item, ...media} : item)
        });
    }

    @Action(CleanStoredMedia)
    cleanStoredMedia(ctx: StateContext<MediaStateModel>): void {
        ctx.patchState({medias: []});
    }

    @Action(SetSelectedMediaType)
    setSelectedMediaType(ctx: StateContext<MediaStateModel>, {type}: SetSelectedMediaType): void {
        const onlyMedias = !!type;
        ctx.patchState({selectedMediaType: type, onlyMedias, currentPage: 1});
    }

    @Action(ChangeMediaPerPage)
    changeMediaPerPage(ctx: StateContext<MediaStateModel>, {mediaPerPage}: ChangeMediaPerPage): Observable<void> {
        this.setMediaPerPage(ctx, {mediaPerPage});
        return ctx.dispatch(new FetchMedia());
    }

    @Action(ChangeCurrentMediaPage)
    changeCurrentMediaPage(ctx: StateContext<MediaStateModel>, {currentPage}: ChangeCurrentMediaPage): Observable<any> {
        ctx.patchState({currentPage});

        return ctx.dispatch(new FetchMedia());
    }

    @Action(SetMediaPerPage)
    setMediaPerPage(ctx: StateContext<MediaStateModel>, {mediaPerPage}: ChangeMediaPerPage): MediaStateModel {
        this.cookie.put('mediaPerPage', mediaPerPage.toString());

        ctx.patchState({currentPage: 1});

        return ctx.patchState({mediaPerPage});
    }

    @Action(SearchMedia)
    searchMedia(ctx: StateContext<MediaStateModel>, {searchParam}: SearchMedia): Observable<void> {
        ctx.patchState({searchParam, currentPage: 1});

        return ctx.dispatch([
            new FetchMedia()
        ]);
    }

    @Action(SetTargetFolderPath)
    setTargetFolderPath(ctx: StateContext<MediaStateModel>, {path}: SetTargetFolderPath): void {
        const state = ctx.getState();
        const homeId = this.store.selectSnapshot(UserSelectors.getMediaHomeDirId);
        const proofOnly = state.currentDirectory?.id === path.id && path.id === homeId && state.proofOnly;
        ctx.patchState({currentDirectory: path, currentPage: 1, proofOnly});
    }

    // Created for avoid issue with ngxs router, ngxs is canceled routing through router link
    @Action(CleanSearchMediaParam)
    cleanSearchMedia(ctx: StateContext<MediaStateModel>): MediaStateModel {
        return ctx.patchState({searchParam: ''});
    }

    @Action(ToggleMediaView)
    toggleMediaView(ctx: StateContext<MediaStateModel>, {type}: ToggleMediaView): void {
        const state = ctx.getState();
        ctx.setState({
            ...state, viewType: type
        });
    }

    @Action(DeleteMedia, {cancelUncompleted: true})
    deleteMedia(ctx: StateContext<MediaStateModel>, {mediaIds, directoryId, sourceFolders}: DeleteMedia): Observable<void> {
        this.store.dispatch(new ToggleGlobalLoader(true));
        const state = ctx.getState();
        const limit = ctx.getState().mediaPerPage;
        const sources = sourceFolders ?? [{folderId: directoryId, mediaIds}];
        return from(sources).pipe(
            concatMap(source => this.mediaService.deleteMediaFromFolder(source.folderId, source.mediaIds)),
            toArray(),
            tap(() => ctx.patchState({
                currentPage: this.getCurrentPageAfterDelete(
                    state.currentPage,
                    limit,
                    state.contentItemAmount,
                    mediaIds.length
                )
            })),
            switchMap(() => ctx.dispatch(new FetchMedia())),
            finalize(() => this.store.dispatch(new ToggleGlobalLoader(false)))
        );
    }

    @Action(DeleteFolders)
    deleteFolders(ctx: StateContext<MediaStateModel>, {ids}: DeleteFolders): Observable<void> {
        this.store.dispatch(new ToggleGlobalLoader(true));
        return this.mediaService.deleteMediaFolders(ids).pipe(
            tap(() => {
                const state = ctx.getState();
                ctx.patchState({
                    currentPage: this.getCurrentPageAfterDelete(
                        state.currentPage,
                        state.mediaPerPage,
                        state.contentItemAmount,
                        ids.length
                    )
                });
            }),
            switchMap(() => ctx.dispatch([new FetchMedia(), new ToggleGlobalLoader(false)]))
        );
    }

    @Action(EditMedia, {cancelUncompleted: true})
    editMedia(ctx: StateContext<MediaStateModel>, {media}: EditMedia): Observable<void> {
        return this.mediaService.editMedia(media)
            .pipe(
                mergeMap(() => ctx.dispatch(new FetchMedia()))
            );
    }

    @Action(SortMediasContent)
    sortMediasContent(ctx: StateContext<MediaStateModel>, {order, sort}: SortMediasContent): Observable<void> {
        ctx.patchState({order, sort});
        return ctx.dispatch(new FetchMedia());
    }

    @Action(SetMediaDefaultPerPage)
    setMediaDefaultPerPage(ctx: StateContext<MediaStateModel>, {perPage}: SetMediaDefaultPerPage): void {
        ctx.patchState({mediaPerPage: perPage || itemsPerPage[5]});
    }

    @Action(ChangeSearchMediaParam)
    changeSearchMediaParam(ctx: StateContext<MediaStateModel>, {searchParam}: ChangeSearchMediaParam): void {
        ctx.patchState({searchParam, currentPage: 1});
    }

    @Action(CleanMedias)
    cleanMedias(ctx: StateContext<MediaStateModel>): void {
        ctx.patchState({medias: [], currentPage: 1, searchParam: ''});
    }

    @Action(ResetMediaState)
    resetMediaState(ctx: StateContext<MediaStateModel>): void {
        const perPageOldValue = ctx.getState().mediaPerPage;
        const directory = ctx.getState().currentDirectory;
        ctx.setState({...initialState, mediaPerPage: perPageOldValue, currentDirectory: directory});
    }

    private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
        const totalAfterDelete = Math.max(total - deletedCount, 0);
        const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

        return Math.min(currentPage, lastAvailablePage);
    }
}
